/**
 * NetworkMonitorPageHeader — page-header chrome shared by every
 * per-chain monitor.
 *
 * Renders:
 *   - left side: chain icon + page title (h1) + subtitle paragraph
 *   - right side: optional Refresh button + a primary action
 *     (typically "Add address" / "Cancel")
 *
 * The primary action is a single button whose label, icon, onClick
 * and visual variant are driven by props so the caller can flip
 * between "Add" and "Cancel" without us caring about the form state.
 *
 * Per-chain colour identity is injected via the `colors` prop.
 */

import type { ReactNode } from 'react';
import type { NetworkColors } from './types';

interface ActionButton {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  /** `'primary'` paints with the chain gradient; `'secondary'` is the
   *  ghost button used for Refresh + the "Cancel" state. */
  variant?: 'primary' | 'secondary';
}

interface NetworkMonitorPageHeaderProps {
  /** Chain icon rendered before the title (e.g. `<Icons.Algorand size={36} />`). */
  icon: ReactNode;
  /** Page title. Renders as the page `<h1>` so screen readers anchor on it. */
  title: string;
  /** Subtitle paragraph under the title. Optional. */
  subtitle?: string;
  /** Primary action button (Add / Cancel). Optional — some monitors
   *  hide it on read-only contexts (e.g. CronosMonitor's Lotto tab). */
  primaryAction?: ActionButton;
  /** Optional Refresh button to the LEFT of the primary action. */
  refreshAction?: { label: string; onClick: () => void; icon: ReactNode };
  /** Per-chain colour palette. */
  colors: NetworkColors;
}

const baseButton = {
  padding: '12px 20px',
  borderRadius: '10px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.2s',
};

const secondaryButton = {
  ...baseButton,
  background: 'rgba(255, 255, 255, 0.1)',
  color: 'var(--text)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
};

export function NetworkMonitorPageHeader({
  icon,
  title,
  subtitle,
  primaryAction,
  refreshAction,
  colors,
}: NetworkMonitorPageHeaderProps) {
  const primaryStyle = primaryAction
    ? primaryAction.variant === 'secondary'
      ? secondaryButton
      : { ...baseButton, background: colors.gradient, color: 'white', border: 'none' }
    : undefined;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
        gap: '20px',
        flexWrap: 'wrap',
      }}
    >
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {icon}
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>{title}</h1>
        </div>
        {subtitle && (
          <p style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>
            {subtitle}
          </p>
        )}
      </div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        {refreshAction && (
          <button onClick={refreshAction.onClick} style={secondaryButton}>
            {refreshAction.icon}
            {refreshAction.label}
          </button>
        )}
        {primaryAction && (
          <button onClick={primaryAction.onClick} style={primaryStyle}>
            {primaryAction.icon}
            {primaryAction.label}
          </button>
        )}
      </div>
    </div>
  );
}
